import { ADDITIONAL_DIAGNOSES } from "./selectValues";
/** @typedef {import("api/typesDef").ClientDiagnosis} ClientDiagnosis */

/**
 * @typedef {Object} DiagnosticFormValues
 * @property {number} diagnosisId - The id of the diagnosis
 * @property {Date} diagnosisDate - The date of the diagnosis
 * @property {string[]} additionalDiagnoses - The additional diagnoses
 * @property {string[]} medicalDiagnoses - The medical diagnoses
 * @property {string} asdSeveritySocial - The ASD severity social communication
 * @property {string} asdSeverityRestricted - The ASD severity restricted, repetitive behaviors
 * @property {string} iqTest - The iq test on file
 * @property {number} iqScore - The full scale iq score
 * @property {string} cars2Type - The cars2 type
 * @property {number} cars2TScore - The cars2 t score
 * @property {string} treatmentModality - The treatment modality
 */

/**
 * The `getDiagnosticFormValues` function returns the form values based on the client diagnosis API response
 * @param {ClientDiagnosis} clientDiagnosis - The client diagnosis API response
 * @returns {DiagnosticFormValues} The form values
 */
function getDiagnosticFormValues(clientDiagnosis) {
  const diagnosisDate = clientDiagnosis?.diagnosis_date
    ? new Date(clientDiagnosis.diagnosis_date)
    : null;

  const additionalDiagnoses = clientDiagnosis?.additional_diagnoses
    ? clientDiagnosis.additional_diagnoses.map((diagnosis) => diagnosis.code)
    : [];

  return {
    diagnosisId: clientDiagnosis?.diagnosis_id || "",
    diagnosisDate,
    additionalDiagnoses,
    medicalDiagnoses: clientDiagnosis?.medical_diagnoses || [],
    asdSeveritySocial: clientDiagnosis?.asd_severity_social || "",
    asdSeverityRestricted: clientDiagnosis?.asd_severity_restricted || "",
    iqTest: clientDiagnosis?.iq_test || "",
    iqScore: clientDiagnosis?.iq_score || "",
    cars2Type: clientDiagnosis?.cars_2_type || "",
    cars2TScore: clientDiagnosis?.cars_2_t_score || "",
    treatmentModality: clientDiagnosis?.treatment_modality || "",
  };
}

/**
 * The `getFormValuesParsedForAPI` function returns the form values parsed for the API
 * @param {DiagnosticFormValues} diagnosticFormValues - The form values
 * @returns {Object} The form values parsed for the API
 */
function getFormValuesParsedForAPI(diagnosticFormValues) {
  const { additionalDiagnoses, cars2TScore, iqScore } = diagnosticFormValues;

  const additional_diagnoses = ADDITIONAL_DIAGNOSES.filter((diagnosis) =>
    additionalDiagnoses.includes(diagnosis.code)
  );

  return {
    diagnosis_id: diagnosticFormValues.diagnosisId || null,
    diagnosis_date: diagnosticFormValues.diagnosisDate || null,
    additional_diagnoses,
    medical_diagnoses: diagnosticFormValues.medicalDiagnoses || [],
    asd_severity_social: diagnosticFormValues.asdSeveritySocial || null,
    asd_severity_restricted: diagnosticFormValues.asdSeverityRestricted || null,
    iq_test: diagnosticFormValues.iqTest || null,
    iq_score: iqScore ? Number(iqScore) : null,
    cars_2_type: diagnosticFormValues.cars2Type || null,
    cars_2_t_score: cars2TScore ? Number(cars2TScore) : null,
    treatment_modality: diagnosticFormValues.treatmentModality || null,
  };
}

export { getDiagnosticFormValues, getFormValuesParsedForAPI };
